import {
  Card,
  Checkbox,
  Col,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  DollarOutlined,
  InfoCircleOutlined,
  TagOutlined,
} from '@ant-design/icons';
import type {NewProgram} from '@PikaDoApp/npm-pikado-logic/dist/modules/program/graphql/program.interface';
import type {Currency} from '@PikaDoApp/npm-pikado-logic/dist/modules/package/graphql/package.interface';

const {Text} = Typography;

export const COUNTRIES: {
  code: 'US' | 'AE' | 'EG' | 'SA' | 'QA' | 'KW' | 'TR';
  flag: string;
  label: string;
  currency: Currency;
  required?: boolean;
}[] = [
  {
    code: 'US',
    flag: '🇺🇸',
    label: 'United States',
    currency: 'USD',
    required: true,
  },
  {code: 'AE', flag: '🇦🇪', label: 'UAE', currency: 'AED'},
  {code: 'EG', flag: '🇪🇬', label: 'Egypt', currency: 'EGP'},
  {code: 'SA', flag: '🇸🇦', label: 'Saudi Arabia', currency: 'SAR'},
  {code: 'QA', flag: '🇶🇦', label: 'Qatar', currency: 'QAR'},
  {code: 'KW', flag: '🇰🇼', label: 'Kuwait', currency: 'KWD'},
  {code: 'TR', flag: '🇹🇷', label: 'Turkey', currency: 'TRY'},
];

export const CURRENCIES: Currency[] = [
  'USD',
  'AED',
  'EGP',
  'SAR',
  'QAR',
  'KWD',
  'TRY',
];

const FLAGS = [
  {name: 'onlyAI', label: 'Only AI', tip: 'Package is AI-exercises only'},
  {name: 'isFree', label: 'Free Package', tip: 'No payment required'},
  {
    name: 'isActive',
    label: 'Active',
    tip: 'Visible and purchasable by students',
  },
];

interface PackageFormFieldsProps {
  programs: any[];
  formValues?: any;
  /** Show review summary card (Create mode) */
  showReview?: boolean;
}

export const PackageFormFields = ({
  programs,
  formValues,
  showReview = false,
}: PackageFormFieldsProps) => {
  return (
    <>
      {/* ── Names ── */}
      <Card
        title={
          <>
            <TagOutlined /> &nbsp; Package Name
          </>
        }
        style={{marginBottom: 16}}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['name_i18n', 'en']}
              label="Name (English)"
              rules={[{required: true, message: 'English name is required'}]}>
              <Input placeholder="e.g. Premium Plan" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={['name_i18n', 'ar']} label="Name (Arabic)">
              <Input placeholder="e.g. الباقة المميزة" dir="rtl" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['description_i18n', 'en']}
              label="Description (English)">
              <Input.TextArea rows={3} placeholder="Describe the package..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name={['description_i18n', 'ar']}
              label="Description (Arabic)">
              <Input.TextArea rows={3} placeholder="وصف الباقة..." dir="rtl" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name={['benefits_i18n', 'en']}
              label="Benefits (English)">
              <Input.TextArea rows={3} placeholder="List the benefits..." />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name={['benefits_i18n', 'ar']} label="Benefits (Arabic)">
              <Input.TextArea
                rows={3}
                placeholder="مزايا الباقة..."
                dir="rtl"
              />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      {/* ── Details ── */}
      <Card
        title={
          <>
            <AppstoreOutlined /> &nbsp; Package Details
          </>
        }
        style={{marginBottom: 16}}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="programId"
              label="Program"
              rules={[{required: true, message: 'Please select a program'}]}>
              <Select
                showSearch
                placeholder="Select a program"
                optionFilterProp="children">
                {programs?.map(prog => {
                  return (
                    <Select.Option key={prog.id} value={prog.id}>
                      {prog.name}
                    </Select.Option>
                  );
                })}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="packageDuration"
              label={
                <span>
                  Duration (months)&nbsp;
                  <Tooltip title="Number of months the package is valid for">
                    <InfoCircleOutlined style={{color: '#aaa'}} />
                  </Tooltip>
                </span>
              }
              rules={[{required: true, message: 'Duration is required'}]}>
              <InputNumber min={1} style={{width: '100%'}} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label="Package Type"
              rules={[{required: true}]}>
              <Select>
                <Select.Option value="STUDENT">Student</Select.Option>
                <Select.Option value="BUSINESS">Business</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="productIAP" label="Product IAP">
              <Input placeholder="e.g. com.pikado.premium_monthly" />
            </Form.Item>
          </Col>
        </Row>

        <Divider orientation="left" plain>
          <Text type="secondary" style={{fontSize: 13}}>
            Flags
          </Text>
        </Divider>

        <Row gutter={16}>
          {FLAGS.map(({name, label, tip}) => (
            <Col span={6} key={name}>
              <Form.Item name={name} valuePropName="checked">
                <Checkbox>
                  {label}&nbsp;
                  <Tooltip title={tip}>
                    <InfoCircleOutlined style={{color: '#ccc', fontSize: 12}} />
                  </Tooltip>
                </Checkbox>
              </Form.Item>
            </Col>
          ))}
        </Row>
      </Card>

      {/* ── Pricing ── */}
      <Card
        title={
          <>
            <DollarOutlined /> &nbsp; Pricing by Country
          </>
        }
        style={{marginBottom: 16}}>
        {COUNTRIES.map(({code, flag, label, currency, required}, idx) => (
          <div key={code}>
            <Row gutter={16} align="middle">
              <Col span={4}>
                <Space>
                  <Text style={{fontSize: 20}}>{flag}</Text>
                  <div>
                    <Text strong>{code}</Text>
                    <br />
                    <Text type="secondary" style={{fontSize: 11}}>
                      {label}
                    </Text>
                  </div>
                  {required && (
                    <Tag color="red" style={{fontSize: 10}}>
                      Required
                    </Tag>
                  )}
                </Space>
              </Col>
              <Col span={10}>
                <Form.Item
                  name={['price', code, 'price']}
                  label="Price"
                  rules={
                    required
                      ? [
                          {required: true, message: 'US price is required'},
                          {type: 'number', min: 0.01, message: 'Must be > 0'},
                        ]
                      : []
                  }
                  style={{marginBottom: 8}}>
                  <InputNumber
                    min={0}
                    style={{width: '100%'}}
                    placeholder={required ? 'Required' : 'Optional'}
                    prefix={<DollarOutlined style={{color: '#ccc'}} />}
                  />
                </Form.Item>
              </Col>
              <Col span={10}>
                <Form.Item
                  name={['price', code, 'currency']}
                  label="Currency"
                  initialValue={currency}
                  style={{marginBottom: 8}}>
                  <Select>
                    {CURRENCIES.map(c => (
                      <Select.Option key={c} value={c}>
                        {c}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            {idx < COUNTRIES.length - 1 && (
              <Divider style={{margin: '8px 0'}} />
            )}
          </div>
        ))}
      </Card>

      {/* ── Review (Create only) ── */}
      {showReview && formValues && (
        <Card
          title={
            <>
              <CheckCircleOutlined /> &nbsp; Review
            </>
          }
          style={{marginBottom: 24}}>
          <Row gutter={[16, 8]}>
            <Col span={12}>
              <Text type="secondary">Name (EN)</Text>
              <br />
              <Text strong>{formValues?.name_i18n?.en || '-'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Name (AR)</Text>
              <br />
              <Text strong>{formValues?.name_i18n?.ar || '-'}</Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Duration</Text>
              <br />
              <Text strong>
                {formValues?.packageDuration
                  ? `${formValues.packageDuration} month(s)`
                  : '-'}
              </Text>
            </Col>
            <Col span={12}>
              <Text type="secondary">Type</Text>
              <br />
              <Tag>{formValues?.type || '-'}</Tag>
            </Col>
            <Col span={24}>
              <Text type="secondary">Flags</Text>
              <br />
              <Space style={{marginTop: 4}}>
                {formValues?.onlyAI && <Tag color="blue">Only AI</Tag>}
                {formValues?.isFree && <Tag color="green">Free</Tag>}
                {formValues?.isActive && <Tag color="cyan">Active</Tag>}
                {!formValues?.onlyAI &&
                  !formValues?.isFree &&
                  !formValues?.isActive && (
                    <Text type="secondary">No flags selected</Text>
                  )}
              </Space>
            </Col>
            <Col span={24}>
              <Text type="secondary">Pricing</Text>
              <br />
              <Space wrap style={{marginTop: 4}}>
                {COUNTRIES.map(({code, flag}) => {
                  const p = formValues?.price?.[code]?.price;
                  const c = formValues?.price?.[code]?.currency;
                  return p > 0 ? (
                    <Tag key={code} color="blue">
                      {flag} {code}: {p} {c}
                    </Tag>
                  ) : null;
                })}
              </Space>
            </Col>
          </Row>
        </Card>
      )}
    </>
  );
};
